"use client";

import { FC, useState } from "react";
import { useAtom } from "jotai";
import {
  DrawerStates,
  VendorDetailsDrawerData,
  drawerAtom,
} from "@/app/_lib/atoms/drawer.atom";
import TextInput from "@/app/_lib/components/forms/text-input";
import ImageUploader from "@/app/_lib/components/cloudinary/image-uploader";
import { useSession } from "next-auth/react";
import { editVendorAction } from "@/app/_lib/actions/vendor.actions";
import CloudImage from "@/app/_lib/components/cloudinary/cloud-image";
import { DocumentTypes, VendorType, ComplianceStatus } from "@/prisma-package/src";
import { MdSearch } from "react-icons/md";
import useDocumentLocator, {
  DocumentLocatorStatus,
} from "@/app/_lib/hooks/use-document-locator";
import useProjectId from "@/app/_lib/hooks/use-project-id";
import { useToast } from "@/app/_lib/hooks/use-toast";
import { useFeaturePermissions } from "@/app/_lib/hooks/use-feature-permissions";
import {
  FeaturesEnum,
  FeatureAccessLevelEnum,
} from "@/app/_lib/types/feature-permissions.types";
import { useOrg } from "@/app/_lib/contexts/org-context";

const VendorDetailsDrawer: FC = () => {
  const projectId = useProjectId() as string;
  const toast = useToast();
  const { data: session } = useSession();
  const { orgId } = useOrg();
  const userId = session?.user?.id;
  const [drawer, setDrawer] = useAtom(drawerAtom);
  const { featurePermissions } = useFeaturePermissions(projectId);
  const isViewOnly =
    featurePermissions[FeaturesEnum.AUDIT] === FeatureAccessLevelEnum.VIEW_ONLY;

  const { docLocateStatus, locateDocument } = useDocumentLocator({
    afterLocate: (results) => {
      if (results && results.length > 0) {
        const firstResult = results[0];
        const documentType = firstResult.type;
        const documentUrl = firstResult.secureUrl;

        setDrawer((state) => {
          switch (documentType) {
            case DocumentTypes.BUSINESS_LICENSE:
              return {
                ...state,
                data: {
                  ...(state.data as VendorDetailsDrawerData),
                  businessLicenseUrl: documentUrl,
                },
              };
            case DocumentTypes.W9:
              return {
                ...state,
                data: {
                  ...(state.data as VendorDetailsDrawerData),
                  w9Url: documentUrl,
                },
              };
            default:
              return state;
          }
        });
      }
    },
  });

  const [formData, setFormData] = useState<VendorDetailsDrawerData>(
    drawer.data as VendorDetailsDrawerData
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const [err, vendor] = await editVendorAction({
      projectId,
      vendor: { ...formData, ein: formData.ein ?? "" },
    });

    if (err) {
      toast({
        message: err.message,
        type: "error",
        state: "active",
      });
    }

    if (vendor) {
      toast({
        message: "Vendor updated successfully",
        type: "success",
        state: "active",
      });

      setDrawer((state) => ({
        ...state,
        state: DrawerStates.DISMISSED,
      }));
    }
  };

  if (!userId || !drawer.data) return null;

  return (
    <div>
      <h2 className="text-2xl pb-4 border-b border-gray-200">
        Vendor Details for {formData.name}
      </h2>
      <form className="flex flex-col py-6" onSubmit={handleSubmit}>
        <div>
          <TextInput
            label="Name:"
            name="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={isViewOnly}
          />
        </div>
        <div className="form-control">
          <label className="label">
            <span className="label-text">Type:</span>
          </label>
          <select
            className="select"
            value={formData.type || ""}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as VendorType || undefined })}
            disabled={isViewOnly}
          >
            <option value="">Select type...</option>
            <option value={VendorType.QUALIFIED}>Qualified</option>
            <option value={VendorType.VERIFIED}>Verified</option>
            <option value={VendorType.PARTNERS}>Partners</option>
          </select>
        </div>
        <div className="form-control">
          <label className="label">
            <span className="label-text">Compliance Status:</span>
          </label>
          <select
            className="select"
            value={formData.complianceStatus || ComplianceStatus.NON_COMPLIANT}
            onChange={(e) => setFormData({ ...formData, complianceStatus: e.target.value as ComplianceStatus })}
            disabled={isViewOnly}
          >
            <option value={ComplianceStatus.COMPLIANT}>Compliant</option>
            <option value={ComplianceStatus.NON_COMPLIANT}>Non-Compliant</option>
          </select>
        </div>
        <div>
          <TextInput
            label="Federal ID (EIN) *:"
            name="ein"
            type="text"
            value={formData.ein ?? undefined}
            onChange={(e) => setFormData({ ...formData, ein: e.target.value })}
            disabled={isViewOnly}
            required
          />
        </div>
        <div>
          <TextInput
            label="Address Line One:"
            name="addressLineOne"
            type="text"
            value={formData.addressLineOne ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, addressLineOne: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="Address Line Two:"
            name="addressLineTwo"
            type="text"
            value={formData.addressLineTwo ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, addressLineTwo: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="City:"
            name="city"
            type="text"
            value={formData.city ?? undefined}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="State:"
            name="state"
            type="text"
            value={formData.state ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, state: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="Zip Code:"
            name="zipCode"
            type="text"
            value={formData.zipCode ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, zipCode: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="Phone:"
            name="phone"
            type="text"
            value={formData.phone ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="Email:"
            name="email"
            type="text"
            value={formData.email ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="Website:"
            name="website"
            type="text"
            value={formData.website ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, website: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div>
          <TextInput
            label="Sales Tax ID:"
            name="salesTaxId"
            type="text"
            value={formData.salesTaxId ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, salesTaxId: e.target.value })
            }
            disabled={isViewOnly}
          />
        </div>
        <div className="flex flex-col gap-1 mt-4 mb-4">
          <span>Business License:</span>
          <input
            name="businessLicenseUrl"
            type="hidden"
            value={formData.businessLicenseUrl ?? undefined}
            onChange={(e) =>
              setFormData({ ...formData, businessLicenseUrl: e.target.value })
            }
          />
          {!formData.businessLicenseUrl ? (
            <div className="py-4">
              <div className="flex items-center gap-4">
                {!isViewOnly ? (
                  <ImageUploader
                    organizationId={orgId ?? undefined}
                    callToAction="Upload Business License"
                    createdBy={userId}
                    documentType={DocumentTypes.BUSINESS_LICENSE}
                    onSuccess={(info) => {
                      if (!info || Array.isArray(info)) return;
                      setDrawer((state) => ({
                        ...state,
                        data: {
                          ...(state.data as VendorDetailsDrawerData),
                          businessLicenseUrl: info.secureUrl,
                        },
                      }));
                    }}
                  />
                ) : null}
                <button
                  className="btn btn-md text-cnd-purple border border-cnd-purple bg-white"
                  type="button"
                  onClick={() =>
                    locateDocument({
                      queryTerms: [formData.name],
                      documentType: DocumentTypes.BUSINESS_LICENSE,
                    })
                  }
                  disabled={docLocateStatus === "loading" || isViewOnly}
                >
                  {docLocateStatus === "loading" ? (
                    "Locating..."
                  ) : (
                    <>
                      <MdSearch /> Auto-locate in Documents
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <CloudImage
              src={formData.businessLicenseUrl}
              width={250}
              height={250}
              alt="Business License"
              documentType={DocumentTypes.BUSINESS_LICENSE}
              viewOnly={isViewOnly}
              onDelete={() => {
                setDrawer((state) => ({
                  ...state,
                  data: {
                    ...(state.data as VendorDetailsDrawerData),
                    businessLicenseUrl: undefined,
                  },
                }));
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-1 mb-8">
          <span>W9:</span>
          <input name="w9Url" type="hidden" value={formData.w9Url ?? ""} />
          {!formData.w9Url ? (
            <div className="py-4">
              <div className="flex items-center gap-4">
                {!isViewOnly ? (
                  <ImageUploader
                    organizationId={orgId ?? undefined}
                    callToAction="Upload W9"
                    createdBy={userId}
                    documentType={DocumentTypes.W9}
                    onSuccess={(info) => {
                      if (!info || Array.isArray(info)) return;
                      setDrawer((state) => ({
                        ...state,
                        data: {
                          ...(state.data as VendorDetailsDrawerData),
                          w9Url: info.secureUrl,
                        },
                      }));
                    }}
                  />
                ) : null}
                <button
                  className="btn btn-md text-cnd-purple border border-cnd-purple bg-white"
                  type="button"
                  onClick={() =>
                    locateDocument({
                      queryTerms: [formData.name],
                      documentType: DocumentTypes.W9,
                    })
                  }
                  disabled={docLocateStatus === "loading" || isViewOnly}
                >
                  {docLocateStatus === "loading" ? (
                    "Locating..."
                  ) : (
                    <>
                      <MdSearch /> Auto-locate in Documents
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <CloudImage
              src={formData.w9Url}
              width={250}
              height={250}
              alt="W9"
              documentType={DocumentTypes.W9}
              viewOnly={isViewOnly}
              onDelete={() => {
                setDrawer((state) => ({
                  ...state,
                  data: {
                    ...(state.data as VendorDetailsDrawerData),
                    w9Url: undefined,
                  },
                }));
              }}
            />
          )}
        </div>
        {docLocateStatus === DocumentLocatorStatus.NoResults && (
          <p className="text-error my-3">
            No matching documents found in the system.
          </p>
        )}
        {!isViewOnly && (
          <button
            className="btn btn-md text-white bg-primary border border-primary"
            type="submit"
          >
            Save Vendor
          </button>
        )}
      </form>
    </div>
  );
};

export default VendorDetailsDrawer;
